'use client';

import { useState } from 'react';
import { Ban, RotateCcw } from 'lucide-react';
import { EditableInput, Wheel } from '@uiw/react-color';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

const FALLBACK_WHEEL_COLOR = '#8b5cf6';

/** Default quick swatches — the hues the OCR chunk-type palette is built from. */
const DEFAULT_PRESETS = [
  '#8b5cf6',
  '#3b82f6',
  '#06b6d4',
  '#10b981',
  '#84cc16',
  '#f59e0b',
  '#ef4444',
  '#ec4899',
  '#64748b',
  '#111827'
];

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

interface ColorPickerPanelProps {
  value: string | null;
  onChange: (hex: string) => void;
  /** Rendered as a "reset" action under the wheel when provided. */
  onReset?: () => void;
  resetLabel?: string;
  /** Called after a reset — lets a host popover/menu close itself. */
  onAfterReset?: () => void;
  /** Overrides the quick swatches, so each surface offers its own palette. */
  presets?: readonly string[];
}

/**
 * The picker itself — wheel, preset swatches, hex input, optional reset.
 * Extracted so it can live inside a popover (Configurations, field cards) or
 * directly inside a context menu (the document viewer's highlight boxes).
 */
export function ColorPickerPanel({
  value,
  onChange,
  onReset,
  resetLabel,
  onAfterReset,
  presets = DEFAULT_PRESETS
}: ColorPickerPanelProps) {
  return (
    <div className='flex flex-col items-center gap-3'>
      <Wheel
        color={value ?? FALLBACK_WHEEL_COLOR}
        width={168}
        height={168}
        onChange={(next) => onChange(next.hex)}
      />

      <div className='flex w-full flex-wrap justify-center gap-1.5'>
        {presets.map((preset) => (
          <button
            key={preset}
            type='button'
            aria-label={preset}
            onClick={() => onChange(preset)}
            className={cn(
              'h-5 w-5 rounded-full border transition-transform hover:scale-110',
              value?.toLowerCase() === preset && 'ring-primary ring-2'
            )}
            style={{ backgroundColor: preset }}
          />
        ))}
      </div>

      <div className='flex w-full items-center gap-2'>
        <span className='text-muted-foreground text-xs'>#</span>
        <EditableInput
          value={value ? value.slice(1) : ''}
          placeholder='8B5CF6'
          className='min-w-0 flex-1'
          inputStyle={{ fontFamily: 'monospace' }}
          onChange={(_evn, next) => {
            if (typeof next !== 'string') return;
            const hex = `#${next.replace(/^#/, '')}`;
            if (HEX_RE.test(hex)) onChange(hex);
          }}
        />
      </div>

      {onReset && (
        <Button
          type='button'
          size='sm'
          variant='ghost'
          className='w-full gap-1.5 text-xs'
          onClick={() => {
            onReset();
            onAfterReset?.();
          }}
        >
          <RotateCcw className='h-3.5 w-3.5' />
          {resetLabel}
        </Button>
      )}
    </div>
  );
}

interface ColorPickerPopoverProps {
  /** Current color as a 6-digit hex, or null when nothing is set. */
  value: string | null;
  onChange: (hex: string) => void;
  /** Rendered as a "reset" action inside the popover when provided. */
  onReset?: () => void;
  resetLabel?: string;
  /** Label on the default trigger button. Ignored when `trigger` is given. */
  label?: string;
  /** Replaces the default trigger button (e.g. a bare swatch). */
  trigger?: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  disabled?: boolean;
  className?: string;
  /** Overrides the quick swatches, so each surface offers its own palette. */
  presets?: readonly string[];
}

/**
 * Color wheel + hex input in a popover. Shared by the OCR default-highlight
 * setting (Configurations), the per-field highlight override on the OCR
 * results page, and the role colour on the Roles page, so every surface
 * picks colours the same way — each with its own swatch palette.
 */
export function ColorPickerPopover({
  value,
  onChange,
  onReset,
  resetLabel,
  label,
  trigger,
  align = 'start',
  disabled,
  className,
  presets
}: ColorPickerPopoverProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger ?? (
          <Button
            type='button'
            variant='outline'
            className={cn('justify-start gap-2', className)}
          >
            {value ? (
              <span
                className='h-4 w-4 shrink-0 rounded-full border'
                style={{ backgroundColor: value }}
              />
            ) : (
              <Ban className='text-muted-foreground h-4 w-4 shrink-0' />
            )}
            <span className='truncate'>{label ?? value ?? ''}</span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className='w-auto' align={align}>
        <ColorPickerPanel
          value={value}
          onChange={onChange}
          onReset={onReset}
          resetLabel={resetLabel}
          onAfterReset={() => setOpen(false)}
          presets={presets}
        />
      </PopoverContent>
    </Popover>
  );
}
